import { useCallback, useContext, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNode } from "@craftjs/core";
import EmptyState from "@/components/EmptyState";
import Settings from "@/components/editor/settings/DishSettings";
import { Spacing } from "@/components/editor/settings/SpacingSettings/SpacingSettings";
import useNormalizeSpacing from "@/components/editor/settings/SpacingSettings/useNormalizeSpacing";
import { SectionContext } from "./section-block/SectionBlock";
import ViewSwitcher from "../../view-switcher/ViewSwitcher";
import useResponsiveValues from "@/components/editor/hooks/useResponsiveValues";
import { useAppSelector } from "@/hooks";
import { useAccessControl } from "@/features/access-control";
import { Category, Content, ContentStatus } from "@/types";

interface DishProps extends Spacing {
  displayType: "list" | "grid" | "carousel";
  selectedContent: Content["id"][];
  limit: number | "all";
  sortBy: "title" | "price";
  sort: "descending" | "ascending";
  selectedCategories: Category["id"][];
  previewWidget: string;
}

const DishBlock = (props: Partial<DishProps>) => {
  const { displayType = "list", selectedCategories, selectedContent, limit = 5, sortBy = "price", sort = "descending", previewWidget } = props;

  const {
    id,
    connectors: { connect, drag },
  } = useNode();

  const params = useParams<{ organizationId?: string; organizationAlias?: string }>();
  const activeOrganization = useAppSelector((store) => store.ui.activeOrganization);
  const sectionSettings = useContext(SectionContext);
  const currentOrganization = useAppSelector((store) => store.organizations[params.organizationId ?? activeOrganization] ?? null);

  const responsive = useResponsiveValues();
  const spacing = useNormalizeSpacing(props);
  const { isActiveUserAdmin } = useAccessControl();

  const publishedContent = useMemo(() => {
    if (!currentOrganization?.content) return [];
    return currentOrganization?.content.filter(({ status, type }) => status === ContentStatus.PUBLISHED && type !== 'offer');
  }, [currentOrganization?.content]);

  const filteredContent = useMemo(() => {
    if (!currentOrganization) return [];
    if (!selectedContent?.length) {
      // filtered by section settings
      if (sectionSettings?.category) {
        return publishedContent.filter((c) => c.category === sectionSettings.category);
      }
      // no filter
      if (!selectedCategories?.length) {
        return publishedContent;
      }
      // filter by categories
      return publishedContent.filter((c) => selectedCategories.includes(c.category));
    }
    // filter by selected content
    return publishedContent
      .filter(({ id }) => selectedContent?.includes(id));
  }, [currentOrganization, selectedContent, publishedContent, sectionSettings.category, selectedCategories]);

  const sortContent = useCallback(
    (contentList: Content[]) => {
      const field = sortBy;
      const acc = sort === "ascending" ? +1 : -1;
      const des = sort === "ascending" ? -1 : +1;

      if (field === "price") {
        return [...contentList].sort((a, b) => {
          if (Number(a[field]) > Number(b[field])) return acc;
          if (Number(a[field]) < Number(b[field])) return des;
          return 0;
        });
      }

      return [...contentList].sort((a, b) => {
        if (a[field] > b[field]) return acc;
        if (a[field] < b[field]) return des;
        return 0;
      });
    },
    [sortBy, sort]
  );

  const sliceContent = useCallback(
    (contentList: Content[]) => {
      return limit === "all" ? contentList : contentList.slice(0, limit);
    },
    [limit]
  );

  const finalContent = useMemo(() => {
    const slicedContent = sliceContent(filteredContent);
    const sortedContent = sortContent(slicedContent);
    return sortedContent;
  }, [filteredContent, sliceContent, sortContent]);

  useEffect(() => {
    if (Array.isArray(selectedCategories)) {
      sectionSettings.setSelectedCategories(selectedCategories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories]);

  if (!finalContent?.length) {
    return isActiveUserAdmin ? <EmptyState title="Categoría vacía" description="Cuando agregues platos/bebidas a esta categoría se verán aquí" /> : null;
  }

  return (
    <div key={`${id}-${responsive.viewport}`} ref={(ref: any) => connect(drag(ref))} className="dish-container" style={{ ...spacing }}>
      <ViewSwitcher data={finalContent as unknown as Content[]} type={displayType} previewWidget={previewWidget} />
    </div>
  );
};

DishBlock.craft = {
  displayName: "Platos",
  related: {
    settings: Settings,
  },
};

export default DishBlock;
